/**
 * reviewUrlState - the review session's position, in the query string.
 *
 * `reviews/{uuid}?doc={id}&finding={uuid}&tab={analysis|notes|activity}`
 *
 * Reloading used to throw away where you were: back to the top of the list,
 * find your place again. The three things that ARE your position now live in
 * the URL, so a refresh restores it and a link carries it to someone else.
 *
 * Deliberately NOT here: pane visibility, widths and docks. Those are a
 * per-user preference that should follow you between sessions - which is what
 * localStorage is for. Pasting your furniture into someone else's window is
 * not what sharing a link should mean.
 *
 * `doc` carries the numeric document id rather than a uuid, because
 * `ReviewDocument` has `id: number` and no uuid field to use.
 *
 * Pure, and separate from the component, because the interesting behaviour is
 * what happens to a BAD value - and that deserves testing without a router.
 */

import {
  FindingTab,
  REVIEW_SURFACE_DEFINITION,
  REVIEW_TABS,
} from './layout';

/**
 * The tab vocabulary is the SURFACE's - see `./layout`. Re-exported here so
 * the call sites that read a position keep one import, but there is exactly
 * one list, and `parseTabParam` checks against it rather than a second copy.
 */
export type { FindingTab };

export const FINDING_TABS: FindingTab[] = REVIEW_TABS;

/** The defaults. A URL states only what these do not already imply. */
export const DEFAULT_TAB: FindingTab = REVIEW_TABS[0];
export const ALL_DOCUMENTS = 'all' as const;

export type SelectedDocument = number | typeof ALL_DOCUMENTS;

/** Anything that is not a known tab reads as the default. */
export function parseTabParam(raw: string | null | undefined): FindingTab {
  return REVIEW_SURFACE_DEFINITION.resolveTab(raw) as FindingTab;
}

/**
 * A `doc` param, or `'all'`.
 *
 * A non-numeric, negative or fractional id is someone else's URL or a typo,
 * and is never trusted enough to become state. A well-formed id still has to
 * survive validation against the session's real documents - that check needs
 * data and so lives in the component.
 */
export function parseDocParam(raw: string | null | undefined): SelectedDocument {
  if (!raw || raw === ALL_DOCUMENTS) return ALL_DOCUMENTS;
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : ALL_DOCUMENTS;
}

/** A `finding` param. Shape is not knowable here - only presence. */
export function parseFindingParam(raw: string | null | undefined): string | null {
  const value = (raw || '').trim();
  return value || null;
}

export interface ReviewPosition {
  document: SelectedDocument;
  finding: string | null;
  tab: FindingTab;
}

/** Read a whole position out of a query string. */
export function readPosition(search: string): ReviewPosition {
  const params = new URLSearchParams(search);
  return {
    document: parseDocParam(params.get('doc')),
    finding: parseFindingParam(params.get('finding')),
    tab: parseTabParam(params.get('tab')),
  };
}

/**
 * The query string a position should be written as.
 *
 * Takes the CURRENT search so params this feature does not own - anything
 * another part of the app put there - survive the write. Defaults are removed
 * rather than spelled out, so the URL stays short and an absent value reads
 * back through the same path a stale one takes.
 *
 * Returns the string WITHOUT a leading `?`, so callers can compare it against
 * `location.search.replace(/^\?/, '')` and skip a no-op navigation.
 */
export function nextSearch(currentSearch: string, position: ReviewPosition): string {
  const params = new URLSearchParams(currentSearch);

  const apply = (key: string, value: string | null) => {
    if (value) params.set(key, value);
    else params.delete(key);
  };

  apply('doc', position.document === ALL_DOCUMENTS ? null : String(position.document));
  apply('finding', position.finding);
  apply('tab', position.tab === DEFAULT_TAB ? null : position.tab);

  return params.toString();
}
